import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { Code, ViewList, Sort as SortIcon } from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { useTheme } from '@/hooks';
import type { LogisticsObjectScope } from '@/types/logisticsObject';
import { VisualQueryBuilder, SortBuilder } from './visual';
import { QueryExpressionToJSON, QueryExpressionFromJSON, SearchLogisticsObjectsRequest, QueryExpression, SortOrder } from '@/api/bol';

export interface QueryBuilderProps {
  value: SearchLogisticsObjectsRequest;
  onChange: (value: SearchLogisticsObjectsRequest) => void;
  disabled?: boolean;
  mode?: 'visual' | 'json' | 'sort';
  logisticsObjectType?: LogisticsObjectScope;
}

type TabValue = 'visual' | 'json' | 'sort';

export const QueryBuilder: React.FC<QueryBuilderProps> = ({
  value,
  onChange,
  disabled = false,
  mode: externalMode,
  logisticsObjectType = 'all'
}) => {
  const { mode: themeMode } = useTheme();
  const [activeTab, setActiveTab] = useState<TabValue>('visual');
  
  // Use external mode if provided, otherwise use internal tab state
  const currentMode = externalMode || activeTab;
  
  // Use ref to track if we're making internal changes
  const isInternalChange = useRef(false);
  const prevModeRef = useRef(currentMode);
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const validationTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  
  // Derive initial query expression from value prop
  const initialQueryExpression = useMemo(() => {
    return value?.query || {};
  }, [value?.query]);
  
  // Derive initial JSON string from value prop
  const initialJsonString = useMemo(() => {
    const jsonBody = {
      ...(value?.query && { query: QueryExpressionToJSON(value.query) }),
      ...(value?.sort && { sort: value.sort }),
      ...(value?.aggregations && { aggregations: value.aggregations })
    };
    return JSON.stringify(jsonBody, null, 2);
  }, [value]);
  
  // Initialize states from derived values
  const [queryExpression, setQueryExpression] = useState<QueryExpression>(initialQueryExpression);
  const [jsonString, setJsonString] = useState(initialJsonString);
  const [sortRules, setSortRules] = useState<Array<{ [key: string]: SortOrder }>>(value?.sort || []);

  // Sync state when value prop changes from parent
  useEffect(() => {
    if (!isInternalChange.current) {
      setQueryExpression(initialQueryExpression);
      setJsonString(initialJsonString);
      setSortRules(value?.sort || []);
    } else {
      isInternalChange.current = false;
    }
  }, [initialQueryExpression, initialJsonString, value?.sort]);

  // Cleanup validation timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    // Only allow internal tab changes if no external mode is provided
    if (externalMode) return;
    
    setActiveTab(newValue);
  };

  // Sync when switching between modes (visual/json/sort)
  useEffect(() => {
    const effectiveMode = externalMode || activeTab;
    const prevMode = prevModeRef.current;
    prevModeRef.current = effectiveMode;
    
    // Only sync if mode actually changed
    if (prevMode === effectiveMode) return;
    
    // When switching to JSON, use current query expression and sort rules
    if (effectiveMode === 'json' && (prevMode === 'visual' || prevMode === 'sort')) {
      const jsonBody: Record<string, unknown> = {};
      
      // Include query if it exists
      if (queryExpression && Object.keys(queryExpression).length > 0) {
        jsonBody.query = QueryExpressionToJSON(queryExpression);
      }
      
      if (sortRules.length > 0) jsonBody.sort = sortRules;
      if (value?.aggregations) jsonBody.aggregations = value.aggregations;
      
      const formatted = JSON.stringify(jsonBody, null, 2);
      setJsonString(formatted);
    }
    
    // When switching to visual, parse current JSON and update the query expression
    if (effectiveMode === 'visual' && prevMode === 'json') {
      try {
        const jsonBody = JSON.parse(jsonString);
        const parsed = jsonBody.query ? QueryExpressionFromJSON(jsonBody.query) : {};
        
        // Update the query expression state so visual builder shows the latest
        setQueryExpression(parsed);
        
        // Update sort rules from JSON
        if (jsonBody.sort) {
          setSortRules(jsonBody.sort);
        }
        
        // Also notify parent component of the change
        const searchBody: SearchLogisticsObjectsRequest = {};
        if (parsed && Object.keys(parsed).length > 0) searchBody.query = parsed;
        if (jsonBody.sort) searchBody.sort = jsonBody.sort;
        if (jsonBody.aggregations) searchBody.aggregations = jsonBody.aggregations;
        
        isInternalChange.current = true;
        onChange(searchBody);
      } catch (error) {
        // Invalid JSON - don't switch to visual mode
        console.error('Error parsing JSON:', error);
      }
    }
    
    // When switching to sort from JSON, parse and update sort rules
    if (effectiveMode === 'sort' && prevMode === 'json') {
      try {
        const jsonBody = JSON.parse(jsonString);
        if (jsonBody.sort) {
          setSortRules(jsonBody.sort);
        }
      } catch (error) {
        console.error('Error parsing JSON:', error);
      }
    }
  }, [activeTab, externalMode, queryExpression, sortRules, jsonString, value, onChange]);

  const handleVisualChange = (newQuery: QueryExpression) => {
    setQueryExpression(newQuery);
    
    // Update parent with complete search body
    const searchBody: SearchLogisticsObjectsRequest = { query: newQuery };
    if (sortRules.length > 0) searchBody.sort = sortRules;
    if (value?.aggregations) searchBody.aggregations = value.aggregations;
    
    isInternalChange.current = true;
    onChange(searchBody);
  };

  const handleSortChange = (newSort: Array<{ [key: string]: SortOrder }>) => {
    setSortRules(newSort);
    
    // Update parent with complete search body
    const searchBody: SearchLogisticsObjectsRequest = {};
    if (queryExpression && Object.keys(queryExpression).length > 0) searchBody.query = queryExpression;
    if (newSort.length > 0) searchBody.sort = newSort;
    if (value?.aggregations) searchBody.aggregations = value.aggregations;
    
    isInternalChange.current = true;
    onChange(searchBody);
  };

  // Debounced validation and update
  const validateAndUpdate = useCallback((jsonValue: string) => {
    try {
      const jsonBody = JSON.parse(jsonValue);
      const parsed = jsonBody.query ? QueryExpressionFromJSON(jsonBody.query) : {};
      
      // Update sort state from JSON
      if (jsonBody.sort) {
        setSortRules(jsonBody.sort);
      }
      
      // Build complete search body
      const searchBody: SearchLogisticsObjectsRequest = {};
      if (parsed && Object.keys(parsed).length > 0) searchBody.query = parsed;
      if (jsonBody.sort) searchBody.sort = jsonBody.sort;
      if (jsonBody.aggregations) searchBody.aggregations = jsonBody.aggregations;
      
      isInternalChange.current = true;
      onChange(searchBody);
    } catch {
      // Invalid JSON - Monaco will show error markers
    }
  }, [onChange]);

  const handleJsonChange = (newValue: string | undefined) => {
    if (newValue === undefined) return;
    
    setJsonString(newValue);
    
    // Clear previous timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    
    // Debounce validation - only validate after 500ms of no typing
    validationTimeoutRef.current = setTimeout(() => {
      validateAndUpdate(newValue);
    }, 500);
  };

  const handleEditorMount = (editor: editor.IStandaloneCodeEditor, monaco: typeof import('monaco-editor')) => {
    editorRef.current = editor;
    
    // Configure JSON language features for better diagnostics
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: false,
      schemas: [],
      enableSchemaRequest: false,
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Tabs - only show if no external mode provided */}
      {!externalMode && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab
              icon={<ViewList />}
              iconPosition="start"
              label="Query"
              value="visual"
              disabled={disabled}
            />
            <Tab
              icon={<SortIcon />}
              iconPosition="start"
              label="Sort"
              value="sort"
              disabled={disabled}
            />
            <Tab
              icon={<Code />}
              iconPosition="start"
              label="JSON"
              value="json"
              disabled={disabled}
            />
          </Tabs>
        </Box>
      )}

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {currentMode === 'visual' && (
          <VisualQueryBuilder
            query={queryExpression}
            onChange={handleVisualChange}
            disabled={disabled}
            logisticsObjectType={logisticsObjectType}
          />
        )}

        {currentMode === 'sort' && (
          <SortBuilder
            value={sortRules}
            onChange={handleSortChange}
            disabled={disabled}
            logisticsObjectType={logisticsObjectType}
          />
        )}

        {currentMode === 'json' && (
          <Box sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
            <Editor
              key="json-editor"
              height="100%"
              defaultLanguage="json"
              value={jsonString}
              onChange={handleJsonChange}
              onMount={handleEditorMount}
              options={{
                readOnly: disabled,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                lineNumbers: 'on',
                folding: true,
                selectOnLineNumbers: true,
                automaticLayout: true,
                fontSize: 12,
                tabSize: 2,
                fixedOverflowWidgets: true,
                hover: {
                  enabled: true,
                  delay: 100
                },
                quickSuggestions: {
                  other: true,
                  comments: false,
                  strings: true
                }
              }}
              theme={themeMode === 'dark' ? 'vs-dark' : 'vs'}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
